import { useQuery } from '@tanstack/react-query';
import type { LookupMode, TimeLogRatesResponse } from '@timelog/pm-shared';

import { useMe } from './hooks';
import { QueryKeys } from './queryKeys';
import { useApiClient } from './useApiClient';

/**
 * Hooks for the read-only TimeLog reference data served under `TIMELOG_MODE`
 * (ADR 0035). Kept out of hooks.ts so the TimeLog surface stays visibly
 * separate from the local-project hook file.
 */

/**
 * `features.timelog` from /api/me — `undefined` while /me is still loading,
 * so callers can distinguish "off" from "not known yet".
 */
export const useTimeLogFeature = (): LookupMode | undefined => {
  const me = useMe();
  return me.data?.features.timelog;
};

/**
 * The tenant default hourly-rate list — the role vocabulary (D1): a ServiceID,
 * a name and the currently valid amount per role. What the Resources popover
 * offers on tasks without a contract or with a fixed-price one, and what seeds
 * a new T&M rate card (D15).
 */
export const useTimeLogRates = (enabled: boolean) => {
  const api = useApiClient();
  const feature = useTimeLogFeature();
  return useQuery({
    queryKey: QueryKeys.TimeLogRates,
    queryFn: () => api.get<TimeLogRatesResponse>('/tlp/rates'),
    enabled: enabled && feature !== undefined && feature !== 'off',
    staleTime: 5 * 60_000,
  });
};
